import React, { useEffect, useState, useRef } from "react";

/* ------------------------------------------------------------------ */
/*  Tiny color utilities                                              */
/* ------------------------------------------------------------------ */
const RECENT_KEY = "pk-recent-colors";
const RECENT_LIMIT = 12;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

export const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
};
export const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map((n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0")).join("");

export const parseColor = (input) => {
  if (!input) return { r: 0, g: 0, b: 0, a: 1 };
  if (input.startsWith("rgba")) {
    const [r, g, b, a] = input.match(/[\d.]+/g).map(Number);
    return { r, g, b, a };
  }
  if (input.startsWith("rgb")) {
    const [r, g, b] = input.match(/[\d.]+/g).map(Number);
    return { r, g, b, a: 1 };
  }
  return { ...hexToRgb(input), a: 1 };
};
export const formatColor = ({ r, g, b, a }) =>
  a < 1 ? `rgba(${r}, ${g}, ${b}, ${Number(a).toFixed(2)})` : rgbToHex(r, g, b);

const loadRecent = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};
export const pushRecentColor = (hex) => {
  if (!hex) return;
  const cur = loadRecent();
  const next = [hex, ...cur.filter((c) => c.toLowerCase() !== hex.toLowerCase())].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) {}
};

/* ------------------------------------------------------------------ */
/*  ColorPicker — hex + R/G/B sliders + opacity + recents             */
/* ------------------------------------------------------------------ */
const ColorPicker = ({ value, onChange, withAlpha = false, allowEmpty = false, label, testid }) => {
  const initial = parseColor(value || "#534AB7");
  const [rgba, setRgba] = useState(initial);
  const [hex, setHex] = useState(rgbToHex(initial.r, initial.g, initial.b));
  const [recent, setRecent] = useState(loadRecent());
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // sync external prop changes
  useEffect(() => {
    const next = parseColor(value || "#534AB7");
    setRgba(next);
    setHex(rgbToHex(next.r, next.g, next.b));
  }, [value]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
        pushRecentColor(rgbToHex(rgba.r, rgba.g, rgba.b));
        setRecent(loadRecent());
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, rgba]);

  const emit = (next) => {
    onChange(formatColor(next));
  };

  const updateChannel = (key, val) => {
    const next = { ...rgba, [key]: clamp(val, 0, key === "a" ? 1 : 255) };
    setRgba(next);
    setHex(rgbToHex(next.r, next.g, next.b));
    emit(next);
  };

  const updateHex = (h) => {
    setHex(h);
    if (/^#?[0-9a-f]{6}$/i.test(h)) {
      const { r, g, b } = hexToRgb(h);
      const next = { r, g, b, a: rgba.a };
      setRgba(next);
      emit(next);
    }
  };

  const swatchBg = formatColor(rgba);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={testid}
        className="w-full h-9 rounded-md border border-[#D3D1C7] flex items-center gap-2 px-2 hover:bg-[#FAFAF9] transition-colors"
      >
        <span
          className="w-5 h-5 rounded-[4px] border border-[#D3D1C7]"
          style={{
            backgroundColor: swatchBg,
            backgroundImage:
              rgba.a < 1
                ? "linear-gradient(45deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(45deg,#ccc 25%,#fff 25%,#fff 75%,#ccc 75%)"
                : undefined,
            backgroundSize: rgba.a < 1 ? "8px 8px" : undefined,
            backgroundPosition: rgba.a < 1 ? "0 0, 4px 4px" : undefined,
          }}
        />
        <span className="text-xs font-mono text-[#2C2C2A] flex-1 text-left truncate">
          {hex.toUpperCase()}{withAlpha && rgba.a < 1 ? ` · ${Math.round(rgba.a * 100)}%` : ""}
        </span>
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute z-50 right-0 mt-1 w-60 bg-white border border-[#D3D1C7] rounded-[10px] shadow-xl p-2.5 space-y-2 max-h-[min(420px,calc(100vh-120px))] overflow-y-auto"
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          <div
            className="h-8 rounded-md border border-[#D3D1C7]"
            style={{
              backgroundColor: swatchBg,
              backgroundImage:
                rgba.a < 1
                  ? "linear-gradient(45deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(45deg,#ccc 25%,#fff 25%,#fff 75%,#ccc 75%)"
                  : undefined,
              backgroundSize: rgba.a < 1 ? "10px 10px" : undefined,
              backgroundPosition: rgba.a < 1 ? "0 0, 5px 5px" : undefined,
            }}
          />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">Hex</div>
            <input
              type="text"
              value={hex}
              onChange={(e) => updateHex(e.target.value)}
              data-testid={testid ? `${testid}-hex` : undefined}
              className="w-full font-mono text-xs px-2 py-1.5 rounded-md bg-[#FAFAF9] border border-[#D3D1C7] focus:outline-none focus:border-[#534AB7]"
              placeholder="#534AB7"
            />
          </div>
          <Slider label="R" value={rgba.r} max={255} color="#D74343" onChange={(v) => updateChannel("r", v)} />
          <Slider label="G" value={rgba.g} max={255} color="#1D9E75" onChange={(v) => updateChannel("g", v)} />
          <Slider label="B" value={rgba.b} max={255} color="#3460D7" onChange={(v) => updateChannel("b", v)} />
          {withAlpha && (
            <Slider
              label="Opacity"
              value={Math.round(rgba.a * 100)}
              max={100}
              color="#534AB7"
              onChange={(v) => updateChannel("a", v / 100)}
              suffix="%"
            />
          )}
          {recent.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">Recent</div>
              <div className="grid grid-cols-6 gap-1">
                {recent.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => updateHex(c)}
                    style={{ backgroundColor: c }}
                    className="w-7 h-7 rounded-md border border-[#D3D1C7] hover:scale-110 transition-transform"
                    aria-label={`Use ${c}`}
                  />
                ))}
              </div>
            </div>
          )}
          {allowEmpty && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="w-full text-[11px] text-[#5F5E5A] underline hover:text-[#2C2C2A]"
            >
              No fill
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Slider = ({ label, value, max, color, onChange, suffix = "" }) => (
  <div>
    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">
      <span>{label}</span>
      <span className="tabular-nums text-[#2C2C2A] font-mono">{value}{suffix}</span>
    </div>
    <input
      type="range"
      min={0}
      max={max}
      step={max === 100 ? 1 : 1}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      style={{ accentColor: color }}
      className="w-full"
    />
  </div>
);

export default ColorPicker;
